/* Shopify price integers are presentment-currency amounts x100, including JPY.
   The app applies discounts server-side. This component never sends a price to /cart/add. */
(() => {
  if (customElements.get('gs-reference-bundle')) return;
  class GsReferenceBundle extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.product = JSON.parse(this.querySelector('[data-product]').textContent);
      this.variants = this.product.variants;
      this.pack = 'duo';
      this.busy = false;
      this.options = [Array(this.product.options.length).fill(null), Array(this.product.options.length).fill(null)];
      this.units = ['eu', 'eu'];
      this.currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
      this.money = new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'en', {style:'currency',currency:this.currency});
      this.currencyDigits = this.money.resolvedOptions().maximumFractionDigits;
      this.base = this.variants.find(v => v.available) || this.variants[0];
      this.abort = new AbortController();
      this.addEventListener('click', e => this.onClick(e), {signal:this.abort.signal});
      this.addEventListener('change', e => this.onChange(e), {signal:this.abort.signal});
      this.querySelector('[data-quiz-form]').addEventListener('submit', e => this.recommend(e), {signal:this.abort.signal});
      this.querySelectorAll('[data-pair]').forEach(pair => {
        pair.addEventListener('toggle', () => {
          if (pair.open) this.querySelectorAll('[data-pair]').forEach(other => { if (other !== pair) other.open = false; });
        }, {signal:this.abort.signal});
        pair.querySelectorAll('[data-kind="size"]').forEach(field => {
          const inputs = [...field.querySelectorAll('[data-value]')];
          if (inputs.every(input => this.parseSize(input.value))) field.querySelector('.gsrb__units').hidden = false;
        });
        pair.querySelectorAll('[data-kind="color"]').forEach(field => this.groupColors(field));
      });
      const initial = this.variants.find(v => String(v.id) === this.dataset.initialVariant);
      if (initial) {
        this.options[0] = [...initial.options];
        this.syncRadios(0);
      }
      if (this.product.options.length === 1 && this.product.options[0] === 'Title' && this.base.title === 'Default Title') {
        this.options = [[this.base.option1], [this.base.option1]];
        this.querySelectorAll('[data-option]').forEach(field => field.hidden = true);
      }
      this.refresh();
      // App embeds can arrive after the product markup. Observe only script additions, not our own UI updates.
      this.appObserver = new MutationObserver(records => {
        if (records.some(r => [...r.addedNodes].some(n => n.nodeType === 1 && (n.matches?.('script.kaching-bundles-deal-block-settings') || n.querySelector?.('script.kaching-bundles-deal-block-settings'))))) this.refresh();
      });
      this.appObserver.observe(document.documentElement, {childList:true,subtree:true});
      window.addEventListener('load', () => this.refresh(), {once:true,signal:this.abort.signal});
    }
    disconnectedCallback() { this.abort?.abort(); this.appObserver?.disconnect(); this.ready = false; }
    fmt(cents) { return this.money.format(cents / 100); }
    roundMoney(cents) { const step = 10 ** (2 - this.currencyDigits); return Math.round(cents / step) * step; }
    parseSize(value) {
      const m = value.match(/^EU\s+(.+?)\s*-\s*US Women\s+(.+?)\s*-\s*US Men\s+(.+)$/i);
      return m ? {eu:m[1],usw:m[2],usm:m[3]} : null;
    }
    groupColors(field) {
      if (field.querySelector('.gsrb__color-groups')) return;
      const limited = JSON.parse(this.querySelector('[data-limited]').textContent).map(v => v.trim().toLowerCase()).filter(Boolean);
      const values = field.querySelector('.gsrb__values');
      const choices = [...values.children];
      const groups = document.createElement('div'); groups.className = 'gsrb__color-groups';
      for (const [isLimited, title] of [[false,'Core'],[true,'Limited Edition']]) {
        const matches = choices.filter(c => limited.includes(c.querySelector('input').value.toLowerCase()) === isLimited);
        if (!matches.length) continue;
        const group = document.createElement('div'); group.className = 'gsrb__color-group';
        const heading = document.createElement('div'); heading.className = 'gsrb__color-heading'; heading.textContent = title;
        const row = document.createElement('div'); row.className = 'gsrb__values'; row.append(...matches);
        group.append(heading,row); groups.append(group);
      }
      values.replaceWith(groups);
    }
    deal() {
      const scripts = document.querySelectorAll('script.kaching-bundles-deal-block-settings');
      for (const script of scripts) {
        if (script.dataset.productId && script.dataset.productId !== this.dataset.productId) continue;
        try {
          const config = JSON.parse(script.textContent);
          const ids = config.selectedProductIds || [];
          if (config.blockVisibility === 'selected-products' && ids.length && !ids.some(id => String(id) === this.dataset.productId)) continue;
          const bar = config.dealBars?.find(b => Number(b.quantity) === 2 && b.dealBarType === 'quantity-break');
          if (bar?.discountType === 'percentage') return {percent:Math.max(0,Math.min(100,Number(bar.discountValue)||0)), config};
        } catch (_) { /* No valid offer: show full price rather than invent a discount. */ }
      }
      return {percent:0,config:null};
    }
    selected(index) {
      return this.variants.find(v => v.options.every((value,i) => value === this.options[index][i]));
    }
    estimate(index) {
      return this.selected(index) || this.variants.find(v => v.available && v.options.every((value,i) => !this.options[index][i] || value === this.options[index][i])) || this.base;
    }
    onClick(e) {
      const pack = e.target.closest('[data-pack]');
      if (pack) { this.pack = pack.dataset.pack; this.refresh(); if (this.pack === 'single') this.querySelector('[data-pair="1"]').open = true; }
      const unit = e.target.closest('[data-size-unit]');
      if (unit) { this.units[Number(unit.closest('[data-pair]').dataset.pair)-1] = unit.dataset.sizeUnit; this.refresh(); }
      if (e.target.closest('[data-atc]')) this.add();
      if (e.target.closest('[data-quiz-open]')) this.openQuiz(Number(e.target.closest('[data-pair]').dataset.pair)-1);
      if (e.target.closest('[data-quiz-close]')) this.querySelector('dialog').close();
      if (e.target.closest('[data-quiz-reset]')) this.resetQuiz();
      if (e.target.closest('[data-quiz-back]')) this.showQuizStep(Math.max(0,this.quizStep-1));
      if (e.target.closest('[data-quiz-apply]')) {
        const field = this.pair(this.quizPair).querySelector('[data-kind="size"]');
        this.options[this.quizPair][Number(field.dataset.option)] = this.recommended;
        this.syncRadios(this.quizPair); this.refresh(); this.querySelector('dialog').close();
      }
    }
    pair(i) { return this.querySelector(`[data-pair="${i+1}"]`); }
    syncRadios(i) {
      this.pair(i).querySelectorAll('[data-option]').forEach(field => {
        field.querySelectorAll('[data-value]').forEach(input => input.checked = input.value === this.options[i][Number(field.dataset.option)]);
      });
    }
    onChange(e) {
      if (!e.target.matches('[data-value]')) return;
      const field = e.target.closest('[data-option]'), i = Number(field.closest('[data-pair]').dataset.pair)-1, position = Number(field.dataset.option);
      this.options[i][position] = e.target.value;
      // Color changes can invalidate size. Clear incompatible dependent options, never purchase a stale variant.
      if (field.dataset.kind === 'color') {
        this.options[i].forEach((value,j) => {
          if (j !== position && value && !this.variants.some(v => v.available && v.options[position] === e.target.value && v.options[j] === value)) this.options[i][j] = null;
        });
        this.syncRadios(i);
      }
      this.refresh();
      const variant = this.selected(i);
      if (variant) {
        const mediaId = variant.featured_media?.id;
        const pi = this.closest('product-info');
        if (mediaId && pi) pi.querySelector('media-gallery')?.setActiveMedia?.(`${pi.dataset.section}-${mediaId}`,true);
        if (i === 0) {
          const url = new URL(location.href); url.searchParams.set('variant',variant.id); history.replaceState({},'',url);
          // Keep payment terms in sync; the custom button is the sole purchase handler.
          pi?.querySelectorAll('input[name="id"]').forEach(input => input.value = variant.id);
          const price = pi?.querySelector('[id^="price-"] .price');
          if (price) {
            const onSale = variant.compare_at_price > variant.price;
            price.classList.toggle('price--on-sale', onSale);
            price.classList.toggle('price--sold-out', !variant.available);
            price.querySelectorAll('.price__regular .price-item--regular, .price-item--sale').forEach(node => node.textContent = this.fmt(variant.price));
            price.querySelectorAll('.price__sale .price-item--regular').forEach(node => node.textContent = this.fmt(variant.compare_at_price || variant.price));
          }
        }
      }
    }
    refresh() {
      const discount = this.deal(); this.discount = discount;
      this.querySelectorAll('[data-pack]').forEach(b => b.setAttribute('aria-pressed',String(b.dataset.pack === this.pack)));
      this.pair(1).hidden = this.pack !== 'duo';
      for (let i=0;i<2;i++) {
        const pair = this.pair(i);
        pair.querySelectorAll('[data-option]').forEach(field => {
          const position = Number(field.dataset.option), kind = field.dataset.kind;
          field.querySelectorAll('[data-value]').forEach(input => {
            const canSelect = this.variants.some(v => v.available && v.options[position] === input.value && v.options.every((value,j) => j === position || kind === 'color' || !this.options[i][j] || value === this.options[i][j]));
            input.disabled = !canSelect || this.busy;
            const label = input.parentElement.querySelector('[data-size-label]');
            if (label) label.textContent = this.parseSize(input.value)?.[this.units[i]] || input.value;
          });
          const chosen = this.options[i][position];
          field.querySelector('[data-selected]').textContent = chosen ? (kind === 'size' ? this.parseSize(chosen)?.[this.units[i]] || chosen : chosen) : '';
          field.querySelectorAll('[data-size-unit]').forEach(b => b.setAttribute('aria-pressed',String(b.dataset.sizeUnit === this.units[i])));
        });
        pair.querySelector('[data-summary]').textContent = this.options[i].filter(Boolean).map(v => this.parseSize(v)?.[this.units[i]] || v).join(' · ');
      }
      const first = this.estimate(0), second = this.estimate(1);
      const totalDuo = this.roundMoney(first.price*(1-discount.percent/100)) + this.roundMoney(second.price*(1-discount.percent/100));
      this.querySelector('[data-single-price]').textContent = this.fmt(first.price);
      const compare = this.querySelector('[data-single-compare]'); compare.hidden = !(first.compare_at_price > first.price); compare.textContent = this.fmt(first.compare_at_price || 0);
      this.querySelector('[data-duo-price]').textContent = `${this.fmt(totalDuo/2)}/pair`;
      this.querySelector('[data-total]').textContent = this.fmt(this.pack === 'duo' ? totalDuo : first.price);
      const count = this.pack === 'duo' ? 2 : 1;
      const missing = Array.from({length:count},(_,i) => i).find(i => !this.selected(i)?.available);
      const atc = this.querySelector('[data-atc]'); atc.disabled = this.busy || missing !== undefined;
      atc.setAttribute('aria-busy',String(this.busy));
      this.querySelector('[data-atc-label]').textContent = this.busy ? 'Adding…' : missing !== undefined ? `Select ${this.dataset.unit} #${missing+1}` : 'Add to Cart';
      this.querySelectorAll('[data-pack]').forEach(b => b.disabled = this.busy);
    }
    async add() {
      if (this.busy) return;
      const chosen = [this.selected(0)]; if (this.pack === 'duo') chosen.push(this.selected(1));
      if (chosen.some(v => !v?.available)) return;
      const items = [];
      chosen.forEach(v => { const existing = items.find(item => item.id === v.id); if (existing) existing.quantity++; else items.push({id:v.id,quantity:1}); });
      const root = this.dataset.root || '/';
      const error = this.querySelector('[data-error]'); error.hidden = true;
      const drawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const payload = {items};
      if (drawer?.getSectionsToRender) { payload.sections = drawer.getSectionsToRender().map(s => s.id); payload.sections_url = location.pathname; drawer.setActiveElement?.(this.querySelector('[data-atc]')); }
      this.busy = true; this.refresh();
      try {
        const response = await fetch(`${root}cart/add.js`,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify(payload)});
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(typeof data.description === 'string' ? data.description : 'Unable to add these items. Please check availability and try again.');
        // After add succeeds, never report a drawer-refresh problem as a failed purchase (which could cause duplicate adds).
        try {
          if (typeof window.upcartRefreshCart === 'function' && typeof window.upcartOpenCart === 'function') { await window.upcartRefreshCart(); window.upcartOpenCart(); }
          else if (window.UpCart?.refresh && window.UpCart?.show) { await window.UpCart.refresh(); window.UpCart.show(); }
          else if (drawer?.renderContents && data.sections && Object.values(data.sections).every(Boolean)) { drawer.classList.remove('is-empty'); drawer.renderContents(data); }
          else location.assign(`${root}cart`);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate,{source:'gs-reference-bundle',cartData:data});
        } catch (_) { location.assign(`${root}cart`); }
      } catch (err) { error.textContent = err.message || 'Unable to add these items. Please try again.'; error.hidden = false; }
      finally { this.busy = false; this.refresh(); }
    }
    openQuiz(index) {
      this.quizPair = index;
      const field = this.pair(index).querySelector('[data-kind="size"]');
      const select = this.querySelector('[data-quiz-form] select[name="size"]'); select.replaceChildren();
      field.querySelectorAll('[data-value]').forEach(input => { const option = new Option(this.parseSize(input.value)?.eu ? `EU ${this.parseSize(input.value).eu}` : input.value,input.value); select.add(option); });
      this.resetQuiz(); this.querySelector('dialog').showModal();
    }
    resetQuiz() { this.querySelector('[data-quiz-form]').hidden = false; this.querySelector('[data-quiz-result]').hidden = true; this.showQuizStep(0); }
    showQuizStep(step) {
      this.quizStep = step;
      this.querySelectorAll('[data-quiz-step]').forEach((el,i) => el.hidden = i !== step);
      this.querySelector('[data-quiz-progress]').textContent = `${step+1} / 4`;
      this.querySelector('[data-quiz-back]').hidden = step === 0;
      this.querySelector('[data-quiz-next]').textContent = step === 3 ? 'Find my size' : 'Continue';
    }
    recommend(e) {
      e.preventDefault(); if (this.quizStep < 3) { this.showQuizStep(this.quizStep+1); return; }
      const form = new FormData(e.target); const select = e.target.elements.size;
      let index = select.selectedIndex;
      if (form.get('fit') === 'tight' || form.get('width') === 'very-wide' || form.get('width') === 'wide') index++;
      else if (form.get('fit') === 'loose') index--;
      index = Math.max(0,Math.min(select.options.length-1,index));
      this.recommended = select.options[index].value;
      this.querySelector('[data-quiz-text]').textContent = `Suggested size: ${select.options[index].textContent}`;
      e.target.hidden = true; this.querySelector('[data-quiz-result]').hidden = false;
    }
  }
  customElements.define('gs-reference-bundle',GsReferenceBundle);
})();
